import { useCallback, useEffect, useRef, useState } from 'react'

/** Whether a value is promise-like, so a save that persists asynchronously can be awaited. */
function isThenable(value: unknown): value is Promise<boolean | void> {
  return typeof (value as { then?: unknown } | null | undefined)?.then === 'function'
}

/**
 * Debounced save with status.
 *
 * When `onSave` persists asynchronously, the reported status follows the write rather than
 * the debounce: `isSaving` stays true until it settles, and `lastSaveTime` only advances
 * once it has actually persisted. Resolving `false` (or rejecting) means it did not, so the
 * caller is never told a value is stored while the request is in flight or after it failed.
 */
export function useAutosave<T>(
  data: T,
  onSave: (data: T) => void | Promise<boolean | void>,
  debounceMs: number = 500,
  /** When false, pending saves are cancelled and new saves are suppressed. Pass `isOpen` for modal usage. */
  isEnabled: boolean = true
): {
  lastSaveTime: number | null
  isSaving: boolean
  /** The most recent save did not persist; whatever `lastSaveTime` reports is older than the current value. */
  saveFailed: boolean
  cancelPendingSave: () => void
  /**
   * Adopt `saved` as the value already on the server, writing it through `write`.
   *
   * For a caller that writes a value itself instead of waiting for the debounce.
   * Cancelling the pending save is not enough: the baseline still holds the
   * superseded value, so the next render reads the caller's own write as a fresh
   * edit and sends the identical payload a second time. Moving the baseline
   * forward makes that render a no-op, and a later genuine edit saves normally.
   *
   * `write` is handed over rather than issued by the caller so the reported status
   * follows it: a failure left over from the value being replaced is cleared once
   * this one persists, and a failure of this one is reported instead of hidden.
   * The baseline moves before `write` runs, so the race above is still closed, and
   * a newer edit scheduled before it settles takes over the status as usual.
   */
  markSaved: (saved: T, write: (saved: T) => void | Promise<boolean | void>) => void
} {
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const isFirstRender = useRef(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dataRef = useRef(data)
  const onSaveRef = useRef(onSave)
  const previousDataRef = useRef<string>('')
  /** Identifies the newest scheduled save, so an older in-flight write cannot report status for it. */
  const saveSequenceRef = useRef(0)

  dataRef.current = data
  onSaveRef.current = onSave

  const serializedData = JSON.stringify(data)

  /**
   * Record the outcome of the write identified by `sequence`.
   *
   * A newer edit has been scheduled, or saving was cancelled, when the sequence has
   * moved on; that write owns the status now and an older one must not report for it.
   */
  const settleSave = useCallback((sequence: number, persisted: boolean) => {
    if (sequence !== saveSequenceRef.current) return
    if (persisted) setLastSaveTime(Date.now())
    setSaveFailed(!persisted)
    setIsSaving(false)
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      previousDataRef.current = serializedData
      return
    }

    if (!isEnabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        setIsSaving(false)
      }
      // Keep baseline current so re-enabling doesn't see a stale diff
      previousDataRef.current = serializedData
      return
    }

    if (previousDataRef.current === serializedData) {
      return
    }

    previousDataRef.current = serializedData

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsSaving(true)
    setSaveFailed(false)

    const sequence = ++saveSequenceRef.current

    timeoutRef.current = setTimeout(() => {
      const settle = (persisted: boolean) => settleSave(sequence, persisted)

      let result: void | Promise<boolean | void>
      try {
        result = onSaveRef.current(dataRef.current)
      } catch {
        settle(false)
        return
      }

      if (!isThenable(result)) {
        settle(true)
        return
      }

      void result.then(
        (persisted) => settle(persisted !== false),
        () => settle(false)
      )
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [serializedData, debounceMs, isEnabled, settleSave])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    // A write already in flight must not report status for a value nobody is waiting on.
    saveSequenceRef.current += 1
    setIsSaving(false)
  }, [])

  const markSaved = useCallback(
    (saved: T, write: (saved: T) => void | Promise<boolean | void>) => {
      cancelPendingSave()
      // Before the write, so the render that observes the caller's own value finds the
      // baseline already moved and does not schedule the identical payload again.
      previousDataRef.current = JSON.stringify(saved)

      // Status follows this write exactly as it follows a debounced one, so a failure
      // left over from the value being replaced stops describing a write that no
      // longer exists, and a failure of this one is still reported.
      setIsSaving(true)
      setSaveFailed(false)
      const sequence = ++saveSequenceRef.current
      const settle = (persisted: boolean) => settleSave(sequence, persisted)

      let result: void | Promise<boolean | void>
      try {
        result = write(saved)
      } catch {
        settle(false)
        return
      }

      if (!isThenable(result)) {
        settle(true)
        return
      }

      void result.then(
        (persisted) => settle(persisted !== false),
        () => settle(false)
      )
    },
    [cancelPendingSave, settleSave]
  )

  return { lastSaveTime, isSaving, saveFailed, cancelPendingSave, markSaved }
}
